// The non-destructive edit model. Every slider/tool writes into this object;
// the WebGL renderer reads it each frame. Persisted as JSON per photo.

export interface HslBand {
  hue: number; // -100..100
  sat: number; // -100..100
  lum: number; // -100..100
}

export type HslColor =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'aqua'
  | 'blue'
  | 'purple'
  | 'magenta';

export const HSL_COLORS: HslColor[] = [
  'red',
  'orange',
  'yellow',
  'green',
  'aqua',
  'blue',
  'purple',
  'magenta',
];

export interface ColorWheel {
  hue: number; // 0..360
  sat: number; // 0..100
  lum: number; // -100..100
}

export interface CropState {
  // Normalised crop rectangle (0..1) relative to the source image.
  x: number;
  y: number;
  width: number;
  height: number;
  angle: number; // straighten, degrees -45..45
  flipH: boolean;
  flipV: boolean;
  rotate90: number; // number of 90° steps, 0..3
}

export interface EditState {
  version: number;

  crop: CropState;

  // Basic — white balance & tone
  temp: number; // -100..100 (cool..warm)
  tint: number; // -100..100 (green..magenta)
  exposure: number; // -5..5 EV
  contrast: number; // -100..100
  highlights: number; // -100..100
  shadows: number; // -100..100
  whites: number; // -100..100
  blacks: number; // -100..100

  // Color
  vibrance: number; // -100..100
  saturation: number; // -100..100
  hsl: Record<HslColor, HslBand>;
  colorGrading: {
    shadows: ColorWheel;
    midtones: ColorWheel;
    highlights: ColorWheel;
    blending: number; // 0..100
    balance: number; // -100..100
  };

  // Detail
  sharpenAmount: number; // 0..150
  sharpenRadius: number; // 0.5..3
  noiseReduction: number; // 0..100 (luminance)
  colorNoiseReduction: number; // 0..100
  texture: number; // -100..100
  clarity: number; // -100..100
  dehaze: number; // -100..100

  // Lens & geometry
  lensDistortion: number; // -100..100
  vignetteAmount: number; // -100..100
  vignetteMidpoint: number; // 0..100
  perspectiveVertical: number; // -100..100
  perspectiveHorizontal: number; // -100..100
  rotate: number; // fine geometry rotate -10..10

  // Local adjustments (each mask carries its own adjustment subset)
  masks: Mask[];

  // Repair (spot removal / clone / heal)
  repairs: RepairSpot[];
}

export type MaskKind = 'brush' | 'linear' | 'radial';

export interface MaskAdjustments {
  exposure: number;
  contrast: number;
  highlights: number;
  shadows: number;
  temp: number;
  tint: number;
  saturation: number;
  clarity: number;
  sharpness: number;
}

export interface BrushStroke {
  x: number;
  y: number;
  radius: number; // normalised
  feather: number;
}

export interface Mask {
  id: string;
  kind: MaskKind;
  name: string;
  inverted: boolean;
  adjustments: MaskAdjustments;
  // Geometry depending on kind (normalised 0..1 coordinates):
  linear?: { x1: number; y1: number; x2: number; y2: number };
  radial?: {
    cx: number;
    cy: number;
    rx: number;
    ry: number;
    feather: number;
  };
  strokes?: BrushStroke[];
}

export interface RepairSpot {
  id: string;
  kind: 'heal' | 'clone';
  x: number; // target center, normalised
  y: number;
  radius: number;
  sourceX: number;
  sourceY: number;
  opacity: number;
}

export const defaultMaskAdjustments = (): MaskAdjustments => ({
  exposure: 0,
  contrast: 0,
  highlights: 0,
  shadows: 0,
  temp: 0,
  tint: 0,
  saturation: 0,
  clarity: 0,
  sharpness: 0,
});

const zeroBand = (): HslBand => ({ hue: 0, sat: 0, lum: 0 });
const zeroWheel = (): ColorWheel => ({ hue: 0, sat: 0, lum: 0 });

export function defaultEditState(): EditState {
  return {
    version: 1,
    crop: {
      x: 0,
      y: 0,
      width: 1,
      height: 1,
      angle: 0,
      flipH: false,
      flipV: false,
      rotate90: 0,
    },
    temp: 0,
    tint: 0,
    exposure: 0,
    contrast: 0,
    highlights: 0,
    shadows: 0,
    whites: 0,
    blacks: 0,
    vibrance: 0,
    saturation: 0,
    hsl: {
      red: zeroBand(),
      orange: zeroBand(),
      yellow: zeroBand(),
      green: zeroBand(),
      aqua: zeroBand(),
      blue: zeroBand(),
      purple: zeroBand(),
      magenta: zeroBand(),
    },
    colorGrading: {
      shadows: zeroWheel(),
      midtones: zeroWheel(),
      highlights: zeroWheel(),
      blending: 50,
      balance: 0,
    },
    sharpenAmount: 0,
    sharpenRadius: 1,
    noiseReduction: 0,
    colorNoiseReduction: 0,
    texture: 0,
    clarity: 0,
    dehaze: 0,
    lensDistortion: 0,
    vignetteAmount: 0,
    vignetteMidpoint: 50,
    perspectiveVertical: 0,
    perspectiveHorizontal: 0,
    rotate: 0,
    masks: [],
    repairs: [],
  };
}

// Merge a persisted (possibly partial / older) settings object over defaults so
// new fields always exist.
export function normalizeEditState(raw: Partial<EditState> | undefined): EditState {
  const base = defaultEditState();
  if (!raw) return base;
  return {
    ...base,
    ...raw,
    crop: { ...base.crop, ...raw.crop },
    hsl: { ...base.hsl, ...raw.hsl },
    colorGrading: { ...base.colorGrading, ...raw.colorGrading },
    masks: raw.masks ?? [],
    repairs: raw.repairs ?? [],
  };
}
